import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Play, Check, ChevronRight, RotateCcw, AlertTriangle, Pin, ExternalLink, Mail, FileText, Paperclip, Users } from 'lucide-react'
import {
  artifacts as artifactsApi,
  pipelineTestStep,
  promptVersions as versionsApi,
  steps as stepsApi,
  type ComputationStep,
  type PipelineTestStepResponse,
  type PipelineTestMatterAssessment,
  type PromptVersion,
} from '../../api'
import { useArtifactPicker } from '../../components/useArtifactPicker'

type StepResult = {
  ok: true
  data: PipelineTestStepResponse
} | {
  ok: false
  error: string
}

export default function PipelineTest() {
  const steps = useQuery({ queryKey: ['steps'], queryFn: () => stepsApi.list() })
  const { filter, setFilter, artifacts: filteredArts, isFetching: artsFetching } = useArtifactPicker({ limit: 50 })
  // Persist selected artifact in the URL query so reload + bookmarks
  // survive. Source of truth is the URL; local state mirrors it for
  // controlled-input ergonomics.
  const [searchParams, setSearchParams] = useSearchParams()
  const urlArtifact = searchParams.get('artifact')
  const [artifactId, setArtifactIdState] = useState<number | ''>(
    urlArtifact ? Number(urlArtifact) : '',
  )
  const setArtifactId = (id: number | '') => {
    setArtifactIdState(id)
    const next = new URLSearchParams(searchParams)
    if (id === '') next.delete('artifact')
    else next.set('artifact', String(id))
    setSearchParams(next, { replace: true })
  }
  // Sync local state when the URL changes externally (browser back, etc.)
  useEffect(() => {
    const cur = urlArtifact ? Number(urlArtifact) : ''
    if (cur !== artifactId) setArtifactIdState(cur)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlArtifact])

  const selectedArtQuery = useQuery({
    queryKey: ['artifact', artifactId],
    queryFn: () => artifactsApi.detail(Number(artifactId)),
    enabled: !!artifactId,
  })

  // Map step_id → result. Persists across navigation within the page.
  const [results, setResults] = useState<Record<number, StepResult>>({})
  // Map step_id → pinned PromptVersion id (or undefined → use production label)
  const [pinned, setPinned] = useState<Record<number, number>>({})

  const ordered = useMemo(() => orderTopological(steps.data ?? []), [steps.data])
  const stepById = useMemo(() => new Map((steps.data ?? []).map((s) => [s.id, s])), [steps.data])

  // Build the upstream_outputs payload for a given step from accumulated results
  function upstreamFor(step: ComputationStep): Record<string, unknown> {
    const out: Record<string, unknown> = {}
    for (const parentId of step.parent_ids) {
      const r = results[parentId]
      const parent = stepById.get(parentId)
      if (r && r.ok && parent) {
        out[parent.name] = r.data.output_parsed ?? {}
      }
    }
    return out
  }

  const run = useMutation({
    mutationFn: ({ step }: { step: ComputationStep }) =>
      pipelineTestStep({
        artifact_id: Number(artifactId),
        step_id: step.id,
        upstream_outputs: upstreamFor(step),
        prompt_version_id: pinned[step.id],
      }),
  })

  function runStep(step: ComputationStep) {
    if (!artifactId) return
    run.mutate({ step }, {
      onSuccess: (data) => {
        setResults((prev) => ({ ...prev, [step.id]: { ok: true, data } }))
      },
      onError: (err) => {
        setResults((prev) => ({ ...prev, [step.id]: { ok: false, error: (err as Error).message } }))
      },
    })
  }

  // Run all steps sequentially in topological order. Each step's output
  // is fed into the next via `upstreamFor()` (which reads `results`).
  // Walks the full DAG including per_open_matter fanouts (L3
  // assess_matter_match) and matter-only steps so the end-to-end
  // behavior matches what the real pipeline would produce for this
  // artifact.
  const [runAllPending, setRunAllPending] = useState(false)
  async function runAll() {
    if (!artifactId || runAllPending) return
    setRunAllPending(true)
    setResults({})
    try {
      for (const step of ordered) {
        try {
          // We rebuild upstream from CURRENT results (post-await), because
          // each iteration mutates `results` via setResults.
          const data = await pipelineTestStep({
            artifact_id: Number(artifactId),
            step_id: step.id,
            upstream_outputs: upstreamFor(step),
            prompt_version_id: pinned[step.id],
          })
          setResults((prev) => ({ ...prev, [step.id]: { ok: true, data } }))
        } catch (e) {
          setResults((prev) => ({ ...prev, [step.id]: { ok: false, error: (e as Error).message } }))
          // Don't halt — the user wants to see how far the chain gets,
          // and downstream steps will gracefully render "missing parent".
        }
      }
    } finally {
      setRunAllPending(false)
    }
  }

  function reset() { setResults({}); setPinned({}) }

  const selectedArt = selectedArtQuery.data ?? null

  return (
    <div className="me-scroll grid h-full grid-cols-[320px_1fr] gap-px overflow-hidden bg-[color:var(--me-border)]">
      {/* Left rail: pick artifact */}
      <aside className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-3">
        <div className="me-section-h">Pick an artifact</div>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="search subject / sender / id…"
          className="me-input mb-2 w-full"
        />
        {artsFetching && filteredArts.length === 0 && (
          <div className="text-[12px] me-text-mute">searching…</div>
        )}
        {!artsFetching && filteredArts.length === 0 && (
          <div className="text-[12px] me-text-mute italic">
            {filter.trim() ? 'No matching artifacts.' : 'No artifacts.'}
          </div>
        )}
        {filteredArts.map((a) => (
          <button
            key={a.id}
            onClick={() => { setArtifactId(a.id); reset() }}
            data-selected={artifactId === a.id ? 'true' : undefined}
            className="me-row mb-0.5 w-full"
          >
            <div className="me-mono text-[10.5px] me-text-mute">{a.composite_id}</div>
            <div className="truncate text-[12px] me-text">{a.subject || '(no subject)'}</div>
            <div className="me-mono mt-0.5 truncate text-[10.5px] me-text-mute">
              {a.artifact_type} · {a.from_name || a.from_email || '—'}
            </div>
          </button>
        ))}
      </aside>

      {/* Right pane: step-by-step */}
      <main className="me-scroll overflow-auto bg-[color:var(--me-bg)] p-5">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h1 className="text-[18px] font-semibold me-text">Pipeline test</h1>
            <p className="text-[12px] me-text-mute">
              Run steps one at a time against a real LLM. Nothing writes to ComputationResult / Run / LlmCallLog —
              this is a sandbox for verifying prompt changes before promoting.
            </p>
          </div>
          <div className="flex items-center gap-1">
            {selectedArt && (
              <button
                onClick={runAll}
                disabled={runAllPending}
                className="me-btn me-btn-primary"
                title="Run every per-artifact step in topological order. Skips matter-fanout steps."
              >
                <Play size={11} />
                {runAllPending ? 'Running…' : 'Run all (topo)'}
              </button>
            )}
            {Object.keys(results).length > 0 && (
              <button onClick={reset} className="me-btn me-btn-outline">
                <RotateCcw size={11} /> Reset
              </button>
            )}
          </div>
        </div>
        {selectedArt && (
          <p className="-mt-2 mb-2 text-[11px] me-text-mute italic">
            Each run is persisted to <code>SandboxRun</code> (visible in
            Studio → Prompts → Recent runs). Production state is untouched.
          </p>
        )}

        {!selectedArt && (
          <div className="me-card p-6 text-center text-[12px] me-text-mute">
            Pick an artifact on the left to begin.
          </div>
        )}

        {selectedArt && (() => {
          const a = selectedArt
          const Icon = a.artifact_type === 'email' ? Mail
            : a.artifact_type === 'attachment' ? Paperclip : FileText
          return (
            <div className="me-card mb-4 p-3">
              <div className="flex items-start gap-2">
                <Icon size={14} className="me-text-mute mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[14px] font-medium me-text truncate flex-1 min-w-0">
                      {a.subject || '(no subject)'}
                    </span>
                    <a
                      href={`/studio/artifact/${a.id}`}
                      target="_blank" rel="noreferrer"
                      className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 inline-flex items-center gap-1 shrink-0"
                      title="Open the full artifact viewer in a new tab"
                    >
                      <ExternalLink size={10} /> view
                    </a>
                  </div>
                  <div className="me-mono mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10.5px] me-text-mute">
                    <span>{a.composite_id}</span>
                    <span>·</span>
                    <span className="uppercase">{a.artifact_type}</span>
                    {a.from_email && <><span>·</span><span className="truncate">{a.from_name || a.from_email}</span></>}
                    {a.source_date && (
                      <><span>·</span><span>{new Date(a.source_date).toLocaleDateString()}</span></>
                    )}
                    {a.parent_artifact && (
                      <><span>·</span><span className="inline-flex items-center gap-1"><Paperclip size={9} />attachment</span></>
                    )}
                  </div>
                  {a.ai_summary && (
                    <div className="mt-1.5 text-[11.5px] leading-snug me-text-dim line-clamp-3">
                      {a.ai_summary.length > 320 ? `${a.ai_summary.slice(0, 320)}…` : a.ai_summary}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })()}

        {selectedArt && ordered.map((step) => (
          <StepCard
            key={step.id}
            step={step}
            stepById={stepById}
            artifactId={Number(artifactId)}
            result={results[step.id]}
            pinnedVersionId={pinned[step.id]}
            onPin={(vid) => setPinned((prev) => {
              const next = { ...prev }
              if (vid) next[step.id] = vid; else delete next[step.id]
              return next
            })}
            onRun={() => runStep(step)}
            running={run.isPending}
            parentResults={results}
          />
        ))}
      </main>
    </div>
  )
}

function StepCard({
  step, stepById, artifactId, result, pinnedVersionId, onPin, onRun, running, parentResults,
}: {
  step: ComputationStep
  stepById: Map<number, ComputationStep>
  artifactId: number
  result?: StepResult
  pinnedVersionId?: number
  onPin: (vid: number | undefined) => void
  onRun: () => void
  running: boolean
  parentResults: Record<number, StepResult>
}) {
  const versions = useQuery({
    queryKey: ['promptVersions', step.slot],
    queryFn: () => versionsApi.bySlot(step.slot),
  })
  // Sandbox versions — any slot with is_sandbox=True. Offered as a
  // cross-slot pin option so an operator can test a sandbox prompt
  // end-to-end through this step without flipping the production
  // version. Backend (apps/pipeline/views.py) accepts cross-slot pins
  // only when the pinned version's slot is a sandbox slot.
  const sandboxVersions = useQuery({
    queryKey: ['promptVersions', 'sandbox'],
    queryFn: () => versionsApi.sandboxOnly(),
  })

  const parentsReady = step.parent_ids.every((pid) => {
    const r = parentResults[pid]
    return r && r.ok
  })
  const canRun = !!artifactId && parentsReady && !running

  const missingParents = step.parent_ids
    .filter((pid) => !parentResults[pid] || !parentResults[pid]?.ok)
    .map((pid) => stepById.get(pid)?.name ?? pid)

  return (
    <div className={`me-card mb-3 p-3 ${result?.ok ? 'border-[color:var(--me-fresh)]' : ''}`}>
      <div className="flex items-center gap-2">
        <span className="me-chip">L{step.level}</span>
        <span className="me-mono text-[12.5px] font-medium me-text">{step.name}</span>
        {step.fanout_kind !== 'none' && <span className="me-chip">{step.fanout_kind}</span>}
        <div className="ml-auto flex items-center gap-1">
          <select
            value={pinnedVersionId ?? ''}
            onChange={(e) => onPin(e.target.value ? Number(e.target.value) : undefined)}
            className="me-input"
            title="Pin a specific prompt version. Includes sandbox slots so you can test a sandbox prompt as a stand-in for this step."
          >
            <option value="">production label</option>
            <optgroup label={`This slot: ${step.name}`}>
              {(versions.data ?? []).map((v: PromptVersion) => (
                <option key={v.id} value={v.id}>
                  v{v.version}{v.labels.includes('production') ? ' [prod]' : ''}
                  {v.commit_message ? ` — ${v.commit_message.slice(0, 30)}` : ''}
                </option>
              ))}
            </optgroup>
            {(sandboxVersions.data ?? []).length > 0 && (
              <optgroup label="Sandbox slots (cross-slot)">
                {(sandboxVersions.data ?? []).map((v: PromptVersion) => (
                  <option key={v.id} value={v.id}>
                    {v.slot_name} v{v.version}
                    {v.commit_message ? ` — ${v.commit_message.slice(0, 28)}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {pinnedVersionId && <Pin size={11} className="me-text-mute" />}
          <button
            onClick={onRun}
            disabled={!canRun}
            className="me-btn me-btn-primary"
            title={!parentsReady ? `Run parents first: ${missingParents.join(', ')}` : ''}
          >
            {result?.ok ? <Check size={11} /> : <Play size={11} />}
            {running ? '…' : (result?.ok ? 'Re-run' : 'Run')}
          </button>
        </div>
      </div>

      {step.parent_ids.length > 0 && (
        <div className="mt-1 text-[11px] me-text-mute">
          parents: {step.parent_ids.map((pid) => {
            const ok = parentResults[pid]?.ok
            const name = stepById.get(pid)?.name ?? `#${pid}`
            return (
              <span key={pid} className={`me-mono ml-1 ${ok ? 'text-[color:var(--me-fresh)]' : 'me-text-mute'}`}>
                {ok ? '✓' : '○'} {name}
              </span>
            )
          })}
        </div>
      )}

      {result && !result.ok && (
        <div className="mt-2 me-card border-[color:var(--me-stale)] p-2 text-[11px] text-[color:var(--me-stale)]">
          <AlertTriangle size={11} className="mr-1 inline" /> {result.error}
        </div>
      )}

      {result && result.ok && result.data.fanout && (
        <FanoutResult data={result.data} />
      )}

      {result && result.ok && !result.data.fanout && (
        <details className="mt-2" open>
          <summary className="me-section-h cursor-pointer hover:me-text">
            output ({result.data.input_tokens ?? 0}+{result.data.output_tokens ?? 0} tokens, {result.data.latency_ms ?? 0}ms)
            {result.data.skipped && <span className="ml-2 me-chip">skipped</span>}
            {result.data.virtual_matter && (
              <span className="ml-2 me-chip" title="L4 chose create_new; this step ran against an in-memory matter synthesized from L4's output. Nothing was saved to the database.">
                virtual matter
              </span>
            )}
            <ChevronRight size={11} className="ml-1 inline" />
          </summary>
          {result.data.skipped && (
            <div className="mt-2 text-[11px] me-text-mute italic">
              Step did not apply to this artifact: {result.data.skip_reason}
            </div>
          )}
          {result.data.virtual_matter && !result.data.skipped && (
            <div className="mt-2 text-[11px] me-text-mute italic">
              Ran against an in-memory virtual matter (L4 chose create_new).
              The matter exists only for this Test Run session — nothing
              was written to the database.
            </div>
          )}
          {/* Output left, Input right — output is what the user is
           *  checking; input is reference. */}
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <div className="me-section-h !mb-1">parsed output</div>
              <pre className="me-mono me-card max-h-72 overflow-auto p-2 text-[10.5px] whitespace-pre-wrap me-text">
                {result.data.output_parsed
                  ? JSON.stringify(result.data.output_parsed, null, 2)
                  : `(unparseable JSON)\n\n${result.data.output_text ?? ''}`}
              </pre>
            </div>
            <div>
              <div className="me-section-h !mb-1">input</div>
              <pre className="me-mono me-card max-h-72 overflow-auto p-2 text-[10.5px] whitespace-pre-wrap me-text-dim">
                {result.data.rendered_input ?? '(no rendered input)'}
              </pre>
            </div>
          </div>
        </details>
      )}
    </div>
  )
}

/** Renders a per_open_matter fan-out result: summary header + ordered
 *  M-NNN list with a does_belong indicator, click a matter row to see
 *  its input + output side-by-side. */
function FanoutResult({ data }: { data: PipelineTestStepResponse }) {
  const assessments = (data.matter_assessments ?? []) as PipelineTestMatterAssessment[]
  const summary = data.summary ?? { matters_evaluated: 0, does_belong_true: 0, errors: 0, total_latency_ms: 0 }
  const [selectedId, setSelectedId] = useState<number | null>(
    assessments[0]?.matter_id ?? null
  )
  const selected = assessments.find((a) => a.matter_id === selectedId) ?? null
  return (
    <div className="mt-2">
      <div className="me-section-h flex items-center gap-2">
        <Users size={11} className="me-text-mute" />
        fanout output — {summary.matters_evaluated} matter(s) evaluated
        <span className="ml-2 me-chip">
          {summary.does_belong_true} does_belong=true
        </span>
        {summary.errors > 0 && (
          <span className="me-chip me-chip-urgent">{summary.errors} errors</span>
        )}
        <span className="ml-auto text-[10.5px] me-text-mute">
          total {Math.round(summary.total_latency_ms / 1000)}s
        </span>
      </div>
      <div className="mt-2 grid grid-cols-[260px_1fr] gap-2">
        {/* Matter picker — ordered by client_seq from the backend. */}
        <div className="me-card max-h-96 overflow-auto p-0">
          {assessments.map((a) => {
            const isSel = a.matter_id === selectedId
            const out = (a.output_parsed ?? {}) as { does_belong?: boolean; confidence?: number }
            const belongs = out.does_belong === true
            const conf = typeof out.confidence === 'number' ? out.confidence : null
            return (
              <button
                key={a.matter_id}
                onClick={() => setSelectedId(a.matter_id)}
                className="me-row w-full px-2 py-1.5 text-left"
                data-selected={isSel ? 'true' : undefined}
              >
                <div className="flex items-center gap-2">
                  <span className="me-mono text-[10.5px] me-text-mute shrink-0">
                    {a.matter_code}
                  </span>
                  {a.status === 'error' ? (
                    <AlertTriangle size={10} className="text-[color:var(--me-stale)] shrink-0" />
                  ) : belongs ? (
                    <Check size={10} className="text-[color:var(--me-fresh)] shrink-0" />
                  ) : (
                    <span className="text-[10px] me-text-mute shrink-0">○</span>
                  )}
                  <span className="text-[11.5px] me-text truncate flex-1 min-w-0">
                    {a.matter_name}
                  </span>
                  {conf !== null && (
                    <span className="me-mono text-[10px] me-text-mute shrink-0">
                      {(conf * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
        {/* Selected matter's input + output */}
        <div>
          {!selected && (
            <div className="me-card p-3 text-[11px] me-text-mute italic">
              Pick a matter on the left to see its input + output.
            </div>
          )}
          {selected && (
            <div>
              <div className="mb-1 flex items-center gap-2 text-[11px] me-text-mute">
                <span className="me-mono">{selected.matter_code}</span>
                <span>·</span>
                <span className="me-text">{selected.matter_name}</span>
                <span className="ml-auto me-mono text-[10px]">
                  {selected.input_tokens}+{selected.output_tokens} tok · {selected.latency_ms}ms
                </span>
              </div>
              {selected.status === 'error' && (
                <div className="mb-1 text-[11px] text-[color:var(--me-stale)]">
                  <AlertTriangle size={10} className="mr-1 inline" />
                  {selected.error_message}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="me-section-h !mb-1">parsed output</div>
                  <pre className="me-mono me-card max-h-72 overflow-auto p-2 text-[10.5px] whitespace-pre-wrap me-text">
                    {selected.output_parsed
                      ? JSON.stringify(selected.output_parsed, null, 2)
                      : `(unparseable JSON)\n\n${selected.output_text}`}
                  </pre>
                </div>
                <div>
                  <div className="me-section-h !mb-1">input</div>
                  <pre className="me-mono me-card max-h-72 overflow-auto p-2 text-[10.5px] whitespace-pre-wrap me-text-dim">
                    {selected.rendered_input}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Topological order respecting parent_ids; ties broken by slot.level then name.
function orderTopological(steps: ComputationStep[]): ComputationStep[] {
  const byId = new Map(steps.map((s) => [s.id, s]))
  const visited = new Set<number>()
  const out: ComputationStep[] = []
  function visit(s: ComputationStep) {
    if (visited.has(s.id)) return
    visited.add(s.id)
    for (const pid of s.parent_ids) {
      const p = byId.get(pid)
      if (p) visit(p)
    }
    out.push(s)
  }
  // Visit in level order so the resulting list reads top-down.
  const sorted = [...steps].sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))
  for (const s of sorted) visit(s)
  return out
}
